/**
 * Tarjeta de Insights de Clientes
 * 
 * Análisis avanzado del comportamiento de clientes con segmentación
 * automática y recomendaciones de retención basadas en datos reales.
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Collapse,
  useTheme
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import {
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Repeat as RepeatIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

/**
 * Colores para los gráficos
 */
const COLORS = {
  new: '#4CAF50',
  returning: '#2196F3',
  vip: '#FF9800',
  churned: '#F44336'
};

/**
 * Componente para mostrar segmento de clientes
 */
const CustomerSegment = ({ segment, count, percentage, trend, color }) => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        p: 2, 
        border: `2px solid ${color}`,
        borderRadius: 2,
        backgroundColor: `${color}10`
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" sx={{ color }}>
          {count}
        </Typography>
        <Box display="flex" alignItems="center">
          {trend > 0 ? (
            <TrendingUpIcon color="success" fontSize="small" />
          ) : (
            <TrendingDownIcon color="error" fontSize="small" />
          )}
          <Typography 
            variant="caption" 
            color={trend > 0 ? 'success.main' : 'error.main'}
            sx={{ ml: 0.5 }}
          >
            {Math.abs(trend)}%
          </Typography>
        </Box>
      </Box>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {segment}
      </Typography>
      
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: theme.palette.grey[200],
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 3
          }
        }}
      />
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {percentage.toFixed(1)}% del total
      </Typography>
    </Box>
  );
};

/**
 * Componente para mostrar top clientes
 */
const TopCustomers = ({ customers = [] }) => {
  if (customers.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No hay datos de clientes disponibles
      </Alert>
    );
  }

  return (
    <List dense>
      {customers.slice(0, 5).map((customer, index) => (
        <React.Fragment key={customer.id || index}>
          <ListItem>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: COLORS.vip }}>
                {index + 1}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" noWrap>
                    {customer.name || `Cliente ${customer.id}`}
                  </Typography>
                  <Chip
                    size="small"
                    label={customer.segment || 'Regular'}
                    color={customer.segment === 'VIP' ? 'warning' : 'default'}
                  />
                </Box>
              }
              secondary={
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {customer.orders || 0} órdenes
                  </Typography>
                  <Typography variant="caption" color="primary">
                    {(customer.totalSpent || 0).toLocaleString('es-AR', {
                      style: 'currency',
                      currency: 'ARS',
                      maximumFractionDigits: 0
                    })}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          {index < customers.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

/**
 * Tooltip personalizado para gráficos
 */
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: 1,
          padding: 2,
          boxShadow: 2
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {data.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clientes: {data.value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Porcentaje: {data.percentage?.toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

/**
 * Componente principal de insights de clientes
 */
const CustomerInsightsCard = ({ data = {}, period = '30 días' }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  // Extraer datos del objeto
  const {
    totalCustomers = 0,
    newCustomers = 0,
    returningCustomers = 0,
    vipCustomers = 0,
    churnedCustomers = 0,
    customerTrends = {},
    topCustomers = [],
    segmentData = [],
    retentionRate = 0,
    averageLifetimeValue = 0
  } = data;

  // Calcular segmentos de clientes
  const segments = useMemo(() => {
    const total = totalCustomers || 1;
    return [
      {
        segment: 'Nuevos Clientes',
        count: newCustomers,
        percentage: (newCustomers / total) * 100,
        trend: customerTrends.new || 0,
        color: COLORS.new
      },
      {
        segment: 'Clientes Recurrentes',
        count: returningCustomers,
        percentage: (returningCustomers / total) * 100,
        trend: customerTrends.returning || 0,
        color: COLORS.returning
      },
      {
        segment: 'Clientes VIP',
        count: vipCustomers,
        percentage: (vipCustomers / total) * 100,
        trend: customerTrends.vip || 0,
        color: COLORS.vip
      },
      {
        segment: 'Clientes Perdidos',
        count: churnedCustomers,
        percentage: (churnedCustomers / total) * 100,
        trend: customerTrends.churned || 0,
        color: COLORS.churned
      }
    ];
  }, [totalCustomers, newCustomers, returningCustomers, vipCustomers, churnedCustomers, customerTrends]);

  // Datos para el gráfico de torta
  const pieData = segments.map(segment => ({
    name: segment.segment,
    value: segment.count,
    percentage: segment.percentage,
    color: segment.color
  })).filter(item => item.value > 0);

  // Generar insights automáticos
  const generateInsights = () => {
    const insights = [];
    
    if (retentionRate > 80) {
      insights.push({
        type: 'success',
        message: `Excelente tasa de retención del ${retentionRate}%`
      });
    } else if (retentionRate < 50) {
      insights.push({
        type: 'warning',
        message: `Tasa de retención baja (${retentionRate}%). Considera estrategias de fidelización`
      });
    }
    
    if (newCustomers > returningCustomers) {
      insights.push({
        type: 'info',
        message: 'Más clientes nuevos que recurrentes. Enfócate en retención'
      });
    }
    
    if (vipCustomers > totalCustomers * 0.1) {
      insights.push({
        type: 'success',
        message: 'Gran base de clientes VIP. Mantén programas de fidelización'
      });
    }
    
    return insights;
  };

  const insights = generateInsights();

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            <Typography variant="h6">Análisis de Clientes</Typography>
          </Box>
        }
        subheader={`Segmentación y comportamiento de los últimos ${period}`}
        action={
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
              }),
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />
      
      <CardContent>
        {/* Métricas principales */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="h6">{totalCustomers}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total Clientes
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                <PersonAddIcon />
              </Avatar>
              <Typography variant="h6">{newCustomers}</Typography>
              <Typography variant="caption" color="text.secondary">
                Nuevos
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1 }}>
                <RepeatIcon />
              </Avatar>
              <Typography variant="h6">{retentionRate}%</Typography>
              <Typography variant="caption" color="text.secondary">
                Retención
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                <StarIcon />
              </Avatar>
              <Typography variant="h6">
                {averageLifetimeValue.toLocaleString('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                  maximumFractionDigits: 0
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                LTV Promedio
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Gráfico de segmentación */}
        {pieData.length > 0 && (
          <Box sx={{ height: 250, mb: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          
          {/* Segmentos detallados */}
          <Typography variant="h6" gutterBottom>
            Segmentación Detallada
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {segments.map((segment, index) => (
              <Grid item xs={12} md={6} key={index}>
                <CustomerSegment {...segment} />
              </Grid>
            ))}
          </Grid>

          {/* Top clientes */}
          <Typography variant="h6" gutterBottom>
            Top Clientes
          </Typography>
          <TopCustomers customers={topCustomers} />

          {/* Insights automáticos */}
          {insights.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Insights Automáticos
              </Typography>
              {insights.map((insight, index) => (
                <Alert 
                  key={index} 
                  severity={insight.type} 
                  variant="outlined"
                  sx={{ fontSize: '0.875rem', mb: 1 }}
                >
                  {insight.message}
                </Alert>
              ))}
            </>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

CustomerInsightsCard.propTypes = {
  data: PropTypes.shape({
    totalCustomers: PropTypes.number,
    newCustomers: PropTypes.number,
    returningCustomers: PropTypes.number,
    vipCustomers: PropTypes.number,
    churnedCustomers: PropTypes.number,
    customerTrends: PropTypes.object,
    topCustomers: PropTypes.array,
    segmentData: PropTypes.array,
    retentionRate: PropTypes.number,
    averageLifetimeValue: PropTypes.number
  }),
  period: PropTypes.string
};

export default CustomerInsightsCard;
